import { UnitType, WasteShipmentLineOutcome, WasteShipmentStatus } from "generated-graphql/graphql";
import { DateTime } from "luxon";

import {
  formatContainerDate,
  formatQuantity,
  NO_VALUE_PLACEHOLDER,
} from "../Containers/containerDisplay";

export { formatQuantity, NO_VALUE_PLACEHOLDER };
export const formatShipmentDate = formatContainerDate;

export const SHIPMENT_STATUS_LABELS: Record<WasteShipmentStatus, string> = {
  [WasteShipmentStatus.Draft]: "Draft",
  [WasteShipmentStatus.Requested]: "Requested",
  [WasteShipmentStatus.Shipped]: "Shipped",
  [WasteShipmentStatus.Canceled]: "Canceled",
};

/**
 * Per-unit totals over whatever lines the caller is showing, in the shape
 * `formatTotalsByUnit` takes. The server's `totalQuantityByUnit` describes the
 * saved shipment, so a view with staged edits has to sum its own lines or the
 * header mixes an edited count with an unedited total.
 */
export function totalsByUnitFor(
  lines: Array<{ quantity: number; unit: UnitType }>
): Array<{ unit: UnitType; total: number }> {
  const byUnit = new Map<UnitType, number>();
  for (const line of lines) {
    byUnit.set(line.unit, (byUnit.get(line.unit) ?? 0) + line.quantity);
  }
  return [...byUnit.entries()]
    .map(([unit, total]) => ({ unit, total }))
    .sort((a, b) => a.unit.localeCompare(b.unit));
}

/** "110 gals + 30 lbs"-style totals; one segment per unit for mixed units. */
export function formatTotalsByUnit(
  totals: Array<{ unit: UnitType; total: number }> | null | undefined
): string {
  if (!totals || totals.length === 0) {
    return NO_VALUE_PLACEHOLDER;
  }
  return totals.map((t) => formatQuantity(t.total, t.unit)).join(" + ");
}

/**
 * Whole days from today (UTC midnight, matching the app's date posture)
 * until the deadline; negative = overdue, null when no deadline computed.
 */
export function daysUntil(deadline: string | Date | null | undefined): number | null {
  if (!deadline) {
    return null;
  }
  const target =
    typeof deadline === "string" ? DateTime.fromISO(deadline) : DateTime.fromJSDate(deadline);
  if (!target.isValid) {
    return null;
  }
  return Math.floor(target.diff(DateTime.utc().startOf("day"), "days").days);
}

export type DeadlineSeverity = "error" | "warning" | "success";

/** Chip color for a days-remaining badge: overdue/≤7d red, ≤30d amber. */
export function deadlineSeverity(days: number): DeadlineSeverity {
  if (days <= 7) {
    return "error";
  }
  if (days <= 30) {
    return "warning";
  }
  return "success";
}

/** A shipment line grouped under its waste stream, with the stream rollup. */
export type StreamGroup<TLine> = {
  key: string;
  streamId: string | null;
  streamName: string | null;
  lines: TLine[];
  /** Per-unit totals, so a mixed-unit stream reads honestly. */
  totals: Array<{ unit: UnitType; total: number }>;
  federalWasteCodes: string[];
  stateWasteCodes: string[];
  dotDescription: string | null;
  profileNumber: string | null;
};

type GroupableLine = {
  quantity: number;
  unit: UnitType;
  federalWasteCodes: string[];
  stateWasteCodes: string[];
  dotDescription?: string | null;
  profileNumber?: string | null;
  wasteStream?: { id: string; name: string } | null;
  container: { containerLabel: string };
};

/**
 * How many distinct waste streams a shipment's lines cover, with everything
 * uncharacterized counting as one stream between them.
 *
 * This is the number to state on the page, because it is the number of headings
 * the contents grid shows: the grid collapses uncharacterized containers under a
 * single heading, since a run of identical ones reads worse than one. Counting
 * `groupLinesByStream` instead disagrees with what the reader can see — that
 * function keys each null stream on its own line on purpose, to mirror how the
 * manifest request buckets them for the vendor.
 */
export function distinctStreamCount(lines: Array<{ wasteStream?: { id: string } | null }>): number {
  return new Set(lines.map((line) => line.wasteStream?.id ?? null)).size;
}

/**
 * Group a shipment's lines the way the vendor-facing manifest request groups
 * them — by waste stream, uncharacterized last — so the internal view and the
 * request read the same. Each group carries the rollup shown on the request:
 * per-unit totals plus the union of the lines' code/DOT/profile snapshots.
 *
 * A null stream keys on its own line so two deleted streams don't collapse
 * into one group, matching `groupManifestRequestLines`.
 */
export function groupLinesByStream<TLine extends GroupableLine>(
  lines: TLine[]
): Array<StreamGroup<TLine>> {
  const groups = new Map<string, StreamGroup<TLine>>();
  lines.forEach((line, index) => {
    const streamId = line.wasteStream?.id ?? null;
    const key = streamId ?? `uncharacterized-${index}`;
    const group = groups.get(key) ?? {
      key,
      streamId,
      streamName: line.wasteStream?.name ?? null,
      lines: [],
      totals: [],
      federalWasteCodes: [],
      stateWasteCodes: [],
      dotDescription: null,
      profileNumber: null,
    };
    group.lines.push(line);
    const existing = group.totals.find((t) => t.unit === line.unit);
    if (existing) {
      existing.total += line.quantity;
    } else {
      group.totals.push({ unit: line.unit, total: line.quantity });
    }
    for (const code of line.federalWasteCodes) {
      if (!group.federalWasteCodes.includes(code)) group.federalWasteCodes.push(code);
    }
    for (const code of line.stateWasteCodes) {
      if (!group.stateWasteCodes.includes(code)) group.stateWasteCodes.push(code);
    }
    group.dotDescription ??= line.dotDescription ?? null;
    group.profileNumber ??= line.profileNumber ?? null;
    groups.set(key, group);
  });

  const ordered = [...groups.values()];
  ordered.sort((a, b) => {
    if (!a.streamName) return 1;
    if (!b.streamName) return -1;
    return a.streamName.localeCompare(b.streamName);
  });
  for (const group of ordered) {
    group.lines.sort((a, b) =>
      a.container.containerLabel.localeCompare(b.container.containerLabel)
    );
    group.totals.sort((a, b) => a.unit.localeCompare(b.unit));
  }
  return ordered;
}

type ShipOutcomeContainer = {
  isPermanent: boolean;
  archivedAt?: string | null;
  amount?: number | null;
  amountUnit?: UnitType | null;
};

/**
 * What shipping did to a container, from the outcome the line recorded when the
 * shipment shipped — the reason a shipped shipment shows an outcome per line
 * instead of an editable amount.
 *
 * The recorded outcome is a past-tense statement, so the copy is too: a
 * container that stayed on site may since have been emptied or archived, and the
 * shipment's row is not the place that tracks that. Lines that shipped before
 * the outcome was recorded fall back to reading current container state — which
 * is what makes it a fallback: it describes the container today, not the day it
 * shipped.
 */
export function containerShipOutcome(line: {
  shipOutcome?: WasteShipmentLineOutcome | null;
  container: ShipOutcomeContainer;
}): { label: string; detail: string | null } {
  const { shipOutcome, container } = line;
  switch (shipOutcome) {
    case WasteShipmentLineOutcome.Archived:
      return { label: "Archived", detail: null };
    case WasteShipmentLineOutcome.Emptied:
      // Only a permanent tank is recorded as emptied — a one-way container that
      // left full is archived instead — so the outcome already carries that,
      // and re-reading isPermanent would only let a later container edit
      // rewrite the label.
      return { label: "Emptied, back in service", detail: null };
    case WasteShipmentLineOutcome.Partial:
      // No remaining amount: the balance at ship time isn't recorded, and the
      // container's current amount would be a different fact wearing its label.
      return { label: "Stayed on site", detail: null };
    default:
      return derivedShipOutcome(container);
  }
}

function derivedShipOutcome(container: ShipOutcomeContainer): {
  label: string;
  detail: string | null;
} {
  if (container.archivedAt) {
    return { label: "Archived", detail: null };
  }
  const remaining = container.amount ?? 0;
  if (container.isPermanent && remaining <= 0) {
    return { label: "Emptied, back in service", detail: null };
  }
  if (remaining > 0) {
    return {
      label: "Still on site",
      detail: formatQuantity(container.amount, container.amountUnit),
    };
  }
  return { label: "Emptied", detail: null };
}

/**
 * Just the unit's abbreviation ("gals"), for use as an input suffix. Derived
 * from the shared quantity formatter so it can't drift from how amounts are
 * printed everywhere else.
 */
export function unitAbbreviation(unit: UnitType): string {
  return formatQuantity(0, unit).replace(/^0\s*/, "");
}

/** One wording for every action that acts on saved state, not staged edits. */
export const SAVE_BEFORE_ACTING = "Save your changes first";
